import React, { useEffect, useState } from 'react';
import _classNames from 'classnames';
import { useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { getAnswersByAccessCode } from 'containers/Assessment/actions';
import {
  makeSelectAnswersError,
  makeSelectAnswersProcessing,
} from 'containers/Assessment/selectors';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import InfoIcon from 'containers/TopicCenter/images/info.svg';
import AppContainer from 'components/AppContainer';
import Tooltip from 'components/SurveyResult/components/ReferralPanel/components/Tooltip';
import useContentfulLocale from 'components/useContentfulLocale';
import useSiteCopySelector from 'components/useSiteCopySelector';
import ctf from 'utils/contentfulService';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: 30,
  },
  gridRightContainer: {
    padding: 20,
    color: '#4c4c4c',
  },
  container: {
    display: 'flex',
    padding: '24px 0px',
  },
  title: {
    margin: '7px 0px',
  },
  button: {
    width: '100%',
    borderRadius: 5,
    height: 48,
    fontFamily: 'MadaRegular',
    fontSize: '1.185rem',
    border: `1px solid ${theme.palette.text.secondary}`,
    '&:hover': {
      opacity: 0.8,
    },
    '& > div': {
      marginLeft: 16,
    },
  },
  gridForm: {
    margin: 10,
    padding: 30,
    background: '#e8f1f5',
    borderRadius: 5,
  },
  cancel: {
    background: '#e8f1f5',
    color: theme.palette.text.secondary,
  },
  save: {
    background: theme.palette.text.secondary,
    color: '#FFFFFF',
  },
  icon: {
    background: '#f8f9f9',
    borderRadius: '50%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '70px',
    width: '70px',
    marginRight: '10px',
    '& img': {
      width: '100%',
      height: '100%',
    },
  },
  textFieldContainer: {
    '& input': {
      fontSize: '1.063rem',
    },
    '& .MuiInputBase-input': {
      background: '#FFFFFF',
      borderRadius: 5,
      padding: 15,
      margin: '8px 0px',
      border: '1px solid transparent',
    },
  },
  error: {
    '& .MuiInputBase-input': {
      border: '1px solid red',
    },
  },
  infoIcon: {
    height: 18,
    width: 18,
    marginLeft: 4,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const queryFn = async (_, params) => {
  const { landingId, ctfLocaleFilter } = params;

  const response = await ctf.getEntries({
    content_type: 'referral',
    'fields.show.sys.id': landingId,
    'fields.style': 'Home Access Code',
    include: 2,
    ...ctfLocaleFilter,
  });

  return response.items[0];
};

const HomeReferral = () => {
  const classes = useStyles();
  const ctfLocaleFilter = useContentfulLocale();
  const dispatch = useDispatch();
  const [siteCopy] = useSiteCopySelector(['assessment-clinical-view']);
  const answersError = useSelector(makeSelectAnswersError());
  const processing = useSelector(makeSelectAnswersProcessing());
  const clientLanding = useSelector(makeSelectClientLanding());
  const [option, setOption] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const queryInfo = useQuery(
    [
      'home-referral',
      {
        landingId: _get(clientLanding, 'sys.id'),
        ctfLocaleFilter,
      },
    ],
    queryFn,
  );
  const { data } = queryInfo;
  const header = _get(data, 'fields.header');
  const otherDetails = _get(data, 'fields.copy');
  const dataList = _get(otherDetails, 'items');

  const errorMessage = _get(siteCopy, 'pageCopy.errorMessage');

  useEffect(() => {
    if (answersError) setError(true);
  }, [answersError]);

  const handleChange = e => {
    if (error) setError(false);
    setAccessCode(e.target.value.toUpperCase());
  };

  const handleSubmit = url => {
    if (!accessCode || accessCode.length < 6) {
      setError(true);
    } else {
      setOption(url);
      dispatch(getAnswersByAccessCode({ accessCode, url }));
    }
  };
  const handleKeyPress = (e, url) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(url);
    }
  };

  return (
    <div>
      <AppContainer>
        <Grid container className={classes.root}>
          <Grid item md={8} className={classes.gridRightContainer}>
            <Typography variant="h3">{header}</Typography>
            {!_isEmpty(dataList) &&
              dataList.map(item => (
                <Grid className={classes.container} key={item.label}>
                  <div className={classes.icon}>
                    <img alt="prior" src={item.icon} />
                  </div>
                  <div>
                    <Typography variant="h5" className={classes.title}>
                      {item.label}
                    </Typography>
                    <Typography>{item.callToAction}</Typography>
                  </div>
                </Grid>
              ))}
          </Grid>
          <Grid item md={4}>
            <Grid className={classes.gridForm}>
              <Typography
                variant="h5"
                color="textPrimary"
                className={classes.header}
              >
                {!_isEmpty(otherDetails) &&
                  _get(otherDetails, 'entryTitle', 'Enter an Access Code')}
                <Tooltip
                  header={_get(otherDetails, 'helpTitle')}
                  body={_get(otherDetails, 'helpBody', '')}
                  hover
                >
                  <img src={InfoIcon} alt="Info" className={classes.infoIcon} />
                </Tooltip>
              </Typography>
              <form noValidate autoComplete="off">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      id="accessCode"
                      classes={{
                        root: _classNames({
                          [classes.textFieldContainer]: true,
                          [classes.error]: error,
                        }),
                      }}
                      onChange={handleChange}
                      onKeyPress={e =>
                        handleKeyPress(e, _get(otherDetails, 'callToActionUrl'))
                      }
                      value={accessCode}
                      fullWidth
                      type="text"
                      margin="dense"
                      placeholder="e.g. 456XYZ"
                      size="medium"
                      inputProps={{ maxLength: 6 }}
                      InputProps={{ disableUnderline: true }}
                      autoComplete="off"
                    />
                    {error && errorMessage && (
                      <Typography variant="overline" color="error">
                        {errorMessage}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
                <Grid container direction="row" spacing={2}>
                  <Grid item xs={12}>
                    {!_isEmpty(otherDetails) && otherDetails.callToActionLabel && (
                      <ButtonBase
                        onClick={() =>
                          handleSubmit(_get(otherDetails, 'callToActionUrl'))
                        }
                        className={_classNames(classes.button, classes.save)}
                      >
                        {!_isEmpty(otherDetails) &&
                          _get(otherDetails, 'callToActionLabel')}
                        {processing &&
                          option === _get(otherDetails, 'callToActionUrl') && (
                            <CircularProgress
                              disableShrink
                              color="inherit"
                              size={16}
                            />
                          )}
                      </ButtonBase>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    {!_isEmpty(otherDetails) &&
                      otherDetails.callToActionLabel2 && (
                        <ButtonBase
                          onClick={() =>
                            handleSubmit(_get(otherDetails, 'callToActionUrl2'))
                          }
                          className={_classNames(
                            classes.button,
                            classes.cancel,
                          )}
                        >
                          {!_isEmpty(otherDetails) &&
                            _get(otherDetails, 'callToActionLabel2')}
                          {processing &&
                            option ===
                              _get(otherDetails, 'callToActionUrl2') && (
                              <CircularProgress
                                disableShrink
                                color="inherit"
                                size={16}
                              />
                            )}
                        </ButtonBase>
                      )}
                  </Grid>
                </Grid>
              </form>
            </Grid>
          </Grid>
        </Grid>
      </AppContainer>
    </div>
  );
};

export default HomeReferral;
